import {Input} from 'antd'
import {getId, getType} from 'apis/model/base'
import FieldsFactory from 'components/form/FieldsFactory'
import Pure from 'components/Pure'
import {useFormikContext} from 'formik'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import React, {useContext} from 'react'
import {Link} from 'react-router-dom'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify} from 'views/AppLocals/factory/createNotification'
import {ask_fetchAskQuestions_Api, ask_updateAsk_Api} from '../../../apis/ask'
import {getLinkToDetail} from '../../../apis/model/ask'
import {StepsForm} from '../../../components/form/StepsForm'
import {FullSideModalLayout} from '../../../components/Modal/SideModalLayout'
import useAsyncAction from '../../../modules/asyncCache/useAsyncAction'
import useTranslate from '../../../modules/local/useTranslate'
import ServerContext from '../../Server/ServerContext'
import {ListHeaderWithLine} from '../../AppLocals/Templates/items/ListHeader'
import PostSettingForm from '../../../components/Feed/PostForm/PostSettingForm'
import {PostFormContext} from '../../../components/Feed/PostForm/PostFormContext'

const tranformBeforeSubmit = (values = {}) => ({...values})

// ─── Step 1 form ──────────────────────────────────────────────────────────────

const AskEditorForm1 = ({onSubmit, isLoading, initialValues}) => {
    const t = useTranslate()

    const {
        resetForm,
        values,
        setSubmitting,
        setErrors,
        initialErrors,
        handleChange,
        ...rest
    } = useFormikContext()

    const {response, handleAsyncAction} = useAsyncAction({
        apiInfo: ask_fetchAskQuestions_Api,
    })

    const debounced = _.debounce((e) => {
        handleAsyncAction({keyword: e.target.value})
    }, 300)

    const suggestions = _.take(_.get(response, 'data.data', []), 7)

    return (
        <div className="flex flex-col gap-6">
            <PostSettingForm/>
            <div className="space-x-1 text-sm italic">
                <span className="font-medium text-color-100">
                    {`* ${t('note')} : `}
                </span>
                <span className="text-color-400">
                    {t('note create ask description')}
                </span>
            </div>
            <div className="flex flex-col gap-3">
                <Pure>
                    <FieldsFactory
                        formSchema={[
                            {
                                children: {
                                    type: 'text',
                                    name: 'title',
                                    label: t('what is your question'),
                                    component: Input,
                                    withProps: {
                                        type: 'text',
                                        minLength: 16,
                                        maxLength: 120,
                                        showCount: true,
                                        placeholder: t('enter your question or concern'),
                                        className: 'rounded-md',
                                        onChange: (e) => {
                                            handleChange(e)
                                            debounced(e)
                                        },
                                    },
                                },
                            },
                        ]}
                    />
                </Pure>
                {!_.isEmpty(suggestions) && (
                    <div className="flex flex-col gap-2 mx-3 border-b pb-2">
                        <ListHeaderWithLine
                            title="questions are already available"
                            className="text-xs"
                        />
                        {suggestions.map((askItem, i) => (
                            <div
                                key={i}
                                style={{padding: '4px 8px'}}
                                className="text-sm italic rounded-md hover:shadow-items-md">
                                <span className="text-color-400">{i + 1}. </span>
                                <Link
                                    target="_blank"
                                    to={getLinkToDetail(askItem)}
                                    className="font-medium text-color-100 no-underline">
                                    {getTitle(askItem)} ?
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <Pure>
                <FieldsFactory
                    formSchema={[
                        {
                            children: {
                                type: 'text',
                                name: 'description',
                                label: t('describe more about your question'),
                                component: Input.TextArea,
                                withProps: {
                                    className: 'scrollBarThin',
                                    type: 'text',
                                    size: 'large',
                                    rows: 5,
                                    maxLength: 500,
                                    showCount: true,
                                    placeholder: t('description'),
                                },
                            },
                        },
                    ]}
                />
            </Pure>
        </div>
    )
}

const AskEditorRoutes = [
    {
        path: '/form-1',
        name: 'make a question',
        component: AskEditorForm1,
        exact: true,
    },
]

// ─── AskEditorForm ────────────────────────────────────────────────────────────

const AskEditorForm = ({onCancel, onSubmit, isLoading, initialValues}) => {
    const t = useTranslate()

    const validate = (values) => {
        let errors = {}
        const title = String(values?.title ?? '')
        if (_.isEmpty(title.trim())) {
            errors.title = t('required field')
        }
        if (title.length < 16) {
            errors.title = t('name required more than {1} characters', {1: 16})
        }
        return errors
    }

    return (
        <StepsForm
            validate={validate}
            isLoading={isLoading}
            validateOnMount={true}
            validateOnChange={true}
            onSubmit={(values) => onSubmit(tranformBeforeSubmit(values))}
            routes={AskEditorRoutes}
            initialValues={initialValues}
            onCancel={onCancel}
        />
    )
}

// ─── EnhancedEditAsk ──────────────────────────────────────────────────────────

export const EnhancedEditAsk = ({askItem, onCancel}) => {
    const t = useTranslate()
    const {options} = useContext(PostFormContext)
    const {item, setCurrent} = useContext(ServerContext)

    const {owner, container} = askItem ?? {}

    const {isLoading, handleAsyncAction: handleAsyncPost} = useAsyncAction({
        apiInfo: ask_updateAsk_Api,
        query: {':ask_id': askItem.id},
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            setCurrent(null)
            onCancel()
        },
        onError: notifyOnError(t),
    })

    // Build owner/container in the Formik option shape
    // so PostSettingForm summary shows correctly and setFieldValue works
    const ownerOption = owner
        ? {
            value: getId(owner),
            data:  owner,
            id:    getId(owner),
            type:  getType(owner),
            label: owner?.name ?? owner?.username ?? '',
        }
        : undefined

    const containerOption = container
        ? options.find((o) => o.value === getId(container)) ?? {
        value: getId(container),
        data:  container,
        id:    getId(container),
        type:  getType(container),
        label: container?.title ?? container?.name ?? '',
    }
        : undefined

    const initialValues = {
        ..._.pick(askItem ?? {}, ['title', 'privacy', 'description']),
        containers: options,
        owner_disabled: true,
        container_disabled: true,
        default_owner_id: getId(owner),
        default_container_id: getId(container),
        // Formik option shape — used by PostSettingForm + submit mapping
        owner: ownerOption,
        container: containerOption,
    }

    const handleClose = () => {
        setCurrent(null)
        onCancel()
    }

    return (
        <FullSideModalLayout
            right
            renderHeader={() => null}
            onCancel={handleClose}>
            <div className="p-3 md:p-6 space-y-3">
                <AskEditorForm
                    initialValues={initialValues}
                    title={t('edit')}
                    isLoading={isLoading}
                    onSubmit={(values) => {
                        const {
                            owner,
                            container,
                            containers,
                            owner_disabled,
                            container_disabled,
                            default_owner_id,
                            default_container_id,
                            ...rest
                        } = values ?? {}

                        handleAsyncPost({
                            ...rest,
                            // Map Formik option shape → API params
                            owner_id:       getId(owner?.data ?? owner),
                            owner_type:     getType(owner?.data ?? owner),
                            container_id:   getId(container?.data ?? container),
                            container_type: getType(container?.data ?? container),
                        })
                    }}
                    onCancel={handleClose}
                />
            </div>
        </FullSideModalLayout>
    )
}

export default AskEditorForm